/**
 * Calendar dates: 'YYYY-MM-DD', a day with no time and no time zone.
 *
 * Comparison is plain string comparison, which for zero-padded ISO dates is chronological order. The
 * arithmetic runs on UTC midnight because UTC has no DST transitions: local arithmetic across a
 * transition can leave the clock at 23:00 the previous day and read back one day short.
 */

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * 'YYYY-MM-DD' exactly as given, or null.
 *
 * Round-trips to reject a date that matches the shape but does not exist. `2026-13-01` gives an
 * Invalid Date, while `2026-02-30` parses and rolls forward to March, so only comparing the result
 * catches the second.
 *
 * @param {*} value
 * @returns {string|null}
 */
export function readCalendarDate(value) {
  if (typeof value !== 'string' || !CALENDAR_DATE.test(value)) {
    return null;
  }
  const asUtc = new Date(`${value}T00:00:00.000Z`);

  if (Number.isNaN(asUtc.getTime())) {
    return null;
  }
  return asUtc.toISOString().slice(0, 10) === value ? value : null;
} // readCalendarDate()

/**
 * The calendar date `days` after the given one. Negative shifts backwards. Null for a date
 * `readCalendarDate` cannot read, since shifting it would invent one.
 *
 * @param {string} calendarDate - 'YYYY-MM-DD'
 * @param {number} days
 * @returns {string|null}
 */
export function addDays(calendarDate, days) {
  const readable = readCalendarDate(calendarDate);

  if (readable === null) {
    return null;
  }
  const shifted = new Date(`${readable}T00:00:00.000Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
} // addDays()

/**
 * How many days separate two calendar dates, negative when `to` precedes `from`. A difference, not a
 * count of days covered: 2026-09-01 to 2026-09-08 is 7. Null when either date is unreadable.
 *
 * @param {string} from - 'YYYY-MM-DD'
 * @param {string} to - 'YYYY-MM-DD'
 * @returns {number|null}
 */
export function daysBetween(from, to) {
  const fromDate = readCalendarDate(from);
  const toDate = readCalendarDate(to);

  if (fromDate === null || toDate === null) {
    return null;
  }
  const span = Date.parse(`${toDate}T00:00:00.000Z`) - Date.parse(`${fromDate}T00:00:00.000Z`);

  return Math.round(span / MILLISECONDS_PER_DAY);
} // daysBetween()
